import React, { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
   ModalProps, Tabs, Tab, Typography, useTheme, 
   useMediaQuery} from '@mui/material';

interface TabbedDialogProps extends ModalProps {
  /**
   * Optional custom cancellation button text
   */
  cancelText?: string | ReactNode;
  /**
   * Optional custom confirmation button text
   */
  confirmText?: string | ReactNode;
  /**
   * The current tab's value
   */
  currentTab: number;
  /**
   * Optional descriptive text to display below the title.
   * Alternately, you can pass children to display custom JSX
   */
  description?: string | ReactNode;
  /**
   * Updates the state of the previous tab to the new tab.
   */
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  /**
   * Allows for the confirm button to be hidden.
   */
  hideConfirmButton?: boolean;
  /**
   * Determines the size (max width) of the dialog
   */
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * Callbacks to handle the user's action.
   * onCancel should close the dialog with no changes,
   * onConfirm should close the dialog and apply the user's changes.
   */
  onCancel: Function;
  onConfirm: Function;
  /**
   * Callback for deletion of records - If this is provided, a delete button will be shown
   * that uses a confirmation dialog before calling onDelete.
   */
  onDelete?: Function;
  /**
   * If `true`, the dialog component is shown.
   */
  open: ModalProps['open'];
  /**
   * Optional styles
   */
  style?: Record<string, any>;
  /**
   * List of the names of the tabs, in desired order
   * @example ['Query & Data', 'Display']
   */
  tabs: string[];
  /**
   * The diaglog's title
   */
  title: string;
}

export default function TabbedDialog(props: TabbedDialogProps) {
  const { confirmText, cancelText, title, onConfirm, hideConfirmButton, onDelete, ...otherProps } = props;
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const onTabChange = (event: React.SyntheticEvent, newValue: number) => props.handleTabChange(event, newValue);

  const handleCancel = (event:any) => props.onCancel(event);
  const handleSubmit = (event:any) => onConfirm(event);

  return (
    <Dialog
      className="drag-cancel"
      fullWidth={true}
      maxWidth={props.maxWidth ?? "md"}
      open={props.open}
      sx={props.style}
    >
      <DialogTitle>
        <Typography variant={"h4"}>
          {props.title}
        </Typography>
        {props.description &&
          <Typography variant="body1">
            {props.description}
          </Typography>}
      </DialogTitle>
      <Box
        sx={{
          padding: 2,
          backgroundColor: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}>
        <Tabs
          value={props.currentTab}
          onChange={onTabChange}>
          {props.tabs.map(item => (
            <Tab key={item} label={item}></Tab>
          ))}
        </Tabs>
      </Box>
      <DialogContent dividers sx={{ pt: 0 }}>
        {props.children}
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button 
          size="small"
          variant="contained"
          color="secondary"
          onClick={(event) => handleCancel(event)}>
          { cancelText || i18n.t("cancel") }
        </Button>
        {!hideConfirmButton &&
          <Button 
            variant="contained"
            color="success"
            size="small"
            onClick={handleSubmit}>
            { confirmText || i18n.t("save") }
          </Button>}
      </DialogActions>
    </Dialog>
  )
}